'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { CaseCard, toCaseCardImage } from '@/components/parts/case-card'
import { FilterPanel, type FilterGroup } from '@/components/parts/filter-panel'
import { COLUMN_BANNERS, RailSidebar } from '@/components/parts/rail-sidebar'
import type { Column } from '@/types/microcms/column'
import type { Seminar } from '@/types/microcms/seminar'
import {
  type CategoryCount,
  type CategoryInfo,
  getColumnCategoryNames
} from '@/utils/categoryMapping'
import {
  DEFAULT_COLUMN_AUTHOR,
  getColumnAuthor,
  getColumnDate
} from '@/utils/column'

type ColumnListSectionProps = {
  columns: Column[]
  /** マッピング順のカテゴリーと件数（getCategoryCounts の戻り値） */
  categories: CategoryCount[]
  seminar?: Seminar
  /** カテゴリー別一覧で表示中のカテゴリー。絞り込みの代わりに見出しを出す */
  currentCategory?: CategoryInfo
  /** SNS 共有に使うページの URL とタイトル */
  share: { url: string; title: string }
}

type Applied = { categories: string[]; keyword: string }

const EMPTY: Applied = { categories: [], keyword: '' }

/**
 * コラム一覧の本体。左に絞り込み + カード 2 列、右に RailSidebar。
 * 絞り込みは「選択した条件で絞り込む」を押したときに反映し、URL（categories / search）にも残す。
 */
export function ColumnListSection({
  columns,
  categories,
  seminar,
  currentCategory,
  share
}: ColumnListSectionProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [keyword, setKeyword] = useState('')
  const [applied, setApplied] = useState<Applied>(EMPTY)
  const [isInitialized, setIsInitialized] = useState(false)

  // URL パラメータから絞り込みを復元する（旧実装と同じ categories / search）
  useEffect(() => {
    if (isInitialized) return

    const params = new URLSearchParams(window.location.search)
    const initial: Applied = {
      categories: params.get('categories')?.split(',').filter(Boolean) ?? [],
      keyword: params.get('search') ?? ''
    }

    setSelected(initial.categories)
    setKeyword(initial.keyword)
    setApplied(initial)
    setIsInitialized(true)
  }, [isInitialized])

  const groups: FilterGroup[] = useMemo(
    () => [
      {
        key: 'categories',
        label: 'カテゴリー',
        items: categories.map((category) => category.displayName)
      }
    ],
    [categories]
  )

  const handleToggle = useCallback((_groupKey: string, item: string) => {
    setSelected((prev) =>
      prev.includes(item)
        ? prev.filter((value) => value !== item)
        : [...prev, item]
    )
  }, [])

  const handleSubmit = useCallback(() => {
    const next: Applied = { categories: selected, keyword: keyword.trim() }
    setApplied(next)

    const url = new URL(window.location.href)
    url.searchParams.delete('categories')
    url.searchParams.delete('search')
    if (next.categories.length > 0) {
      url.searchParams.set('categories', next.categories.join(','))
    }
    if (next.keyword) url.searchParams.set('search', next.keyword)

    window.history.replaceState({}, '', url.pathname + url.search)
  }, [selected, keyword])

  const visibleColumns = useMemo(() => {
    // カテゴリー別一覧は絞り込みを出さないので、URL に条件が残っていても無視する
    const active = currentCategory ? EMPTY : applied
    const values = active.categories.map(
      (name) =>
        categories.find((category) => category.displayName === name)
          ?.microcmsValue ?? name
    )
    const term = active.keyword.toLowerCase()

    return [...columns]
      .sort(
        (a, b) =>
          new Date(getColumnDate(b)).getTime() -
          new Date(getColumnDate(a)).getTime()
      )
      .filter((column) => {
        if (
          values.length > 0 &&
          !getColumnCategoryNames(column.columnCategory).some((name) =>
            values.includes(name)
          )
        ) {
          return false
        }
        if (!term) return true

        return [column.title, column.description].some((field) =>
          field?.toLowerCase().includes(term)
        )
      })
  }, [columns, categories, currentCategory, applied])

  return (
    <section className="bg-surface px-6 py-12 lg:px-panelx lg:py-panelx">
      <div className="mx-auto flex max-w-wide flex-col gap-14 lg:flex-row lg:items-start lg:gap-10 xl:gap-20">
        <div className="flex w-full min-w-0 flex-1 flex-col gap-14">
          {currentCategory ? (
            <h2 className="fontfeaturesettings text-2xl font-bold tracking-heading text-ink">
              <span className="text-ink-subtle">カテゴリー：</span>
              {currentCategory.displayName}
            </h2>
          ) : (
            <FilterPanel
              groups={groups}
              selected={{ categories: selected }}
              onToggle={handleToggle}
              keyword={keyword}
              onKeywordChange={setKeyword}
              onSubmit={handleSubmit}
            />
          )}

          {visibleColumns.length > 0 ? (
            <ul className="grid w-full grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {visibleColumns.map((column) => (
                <CaseCard
                  key={column.id}
                  href={`/column/${column.id}/`}
                  title={column.title}
                  clampTitle
                  tags={getColumnCategoryNames(column.columnCategory).map(
                    (label) => ({ group: 'category', label })
                  )}
                  byline={{
                    date: getColumnDate(column),
                    author:
                      getColumnAuthor(column)?.name || DEFAULT_COLUMN_AUTHOR
                  }}
                  image={toCaseCardImage(column.eyecatch)}
                />
              ))}
            </ul>
          ) : (
            <p className="text-center text-base leading-body text-ink-subtle">
              条件に合うコラムが見つかりませんでした。
            </p>
          )}
        </div>

        <div className="w-full shrink-0 lg:rail-sticky lg:w-80 xl:w-rail">
          <RailSidebar
            banners={COLUMN_BANNERS}
            categories={categories.map((category) => ({
              label: category.displayName,
              href: `/column/categories/${category.slug}/`,
              count: category.count,
              current: category.slug === currentCategory?.slug
            }))}
            seminar={
              seminar
                ? {
                    href: `/seminar/${seminar.id}/`,
                    title: seminar.title,
                    image: toCaseCardImage(seminar.eyecatch)
                  }
                : undefined
            }
            share={share}
          />
        </div>
      </div>
    </section>
  )
}
